import { useState } from 'react'
import { useForm, useWatch, SubmitHandler } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Container, Row, Col, Table, Spinner } from 'react-bootstrap'
import { getRouteApi, useRouteContext } from '@tanstack/react-router'

// Scripts
import { reprocessLea, useCoreData } from '@/queries.ts'
import { getAllLeaData, makeDataFns } from '@/dev/local-data'
import { csvConfigs } from '../models/csv-data'
import { RecordType } from '../models'

// Components
import Card from '@/components/shared/Card'
import LoaderButton from '@/components/shared/LoaderButton'
import LayoutContentArea from '@/components/layout/LayoutContentArea'

export type CreateLocalDataRequest = {
    RecordType: RecordType,
    RecordCount: string,
    PercentNew: string,
}


export default function MakeLocalDataPage() {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const coreData = useCoreData()
    const queryClient = useQueryClient()
    const {
        control,
        handleSubmit,
        register,
        getValues,
        formState: { isDirty, isValid, isSubmitting, isSubmitted, errors }
    } = useForm<CreateLocalDataRequest>({
        defaultValues: {
            RecordType: undefined,
            RecordCount: "0",
            PercentNew: "100",
        },
    });
    const recordType = useWatch({ control, name: 'RecordType' })
    //const programYearFilterExtracts = Object.keys(makeDataFns)

    const onSubmit: SubmitHandler<CreateLocalDataRequest> = async (data: CreateLocalDataRequest) => {
        const leaData = await getAllLeaData(leaContext, leaContext.currentUserOrg.esiIdPath)
        makeDataFns[data.RecordType](coreData, leaData, Number(data.RecordCount), Number(data.PercentNew))
        reprocessLea(leaContext.hasRole, leaData)
        queryClient.setQueryData(['leaData', leaContext.currentUserOrg.esiIdPath], leaData)
    }
    return (
        <LayoutContentArea
            headingText="Create Local Data"
        >
            <Card className="mb-4">
                <div className="fs-5 fw-bold mb-3">Create</div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Container className="ps-3 pe-3 request-container" style={{ borderTop: 'none', borderRadius: 0 }}>
                        <Row className="form-group row">
                            <Col lg={3} className="text-lg-end">
                                Record Type
                            </Col>
                            <Col lg={7}>
                                <select
                                    className="form-select"
                                    defaultValue=""
                                    disabled={isSubmitting}
                                    {...register(`RecordType`, { required: "Record type is required" })}
                                >
                                    <option value="">Select option</option>
                                    {Object.keys(makeDataFns).map(type =>
                                        <option key={type} value={type}>{type}</option>
                                    )}
                                </select>
                            </Col>
                        </Row>
                        <Row className="form-group row">
                            <Col lg={3} className="text-lg-end">
                                Number of Records
                            </Col>
                            <Col lg={7}>
                                <input id="RecordCount" className="form-control" type="text" maxLength={50} {...register('RecordCount')} />
                            </Col>
                        </Row>
                        <Row className="form-group row">
                            <Col lg={3} className="text-lg-end">
                                % of Records Generate New Entities
                            </Col>
                            <Col lg={7}>
                                <input id="PercentNew" className="form-control" type="text" maxLength={5} {...register('PercentNew')} />
                            </Col>
                        </Row>

                    </Container>
                    <div className="text-right">
                        <LoaderButton
                            variant="primary"
                            type="submit"
                            disabled={!(isDirty && isValid) || isSubmitting}
                            loading={isSubmitting}
                        >Create</LoaderButton>
                    </div>
                </form>
            </Card>
        </LayoutContentArea>
    );
}
